import {useCallback} from 'react';
import {useHistory} from 'react-router-dom';

export function useResolveLinkTarget() {
  const history = useHistory();
  return useCallback(
    (targetHref: string) =>
      resolveLinkTarget(targetHref, history.createHref({pathname: '/'})),
    [history]
  );
}

// Converts an absolute URL under the current origin and router base to an
// app-relative path. URLs outside that base remain absolute browser links.
export function resolveLinkTarget(
  targetHref: string,
  routerRoot = '/'
): string {
  const url = new URL(targetHref);
  if (url.origin !== window.location.origin) {
    return targetHref;
  }

  // A URL streamed into markdown can be rendered between chunks with a
  // truncated percent-escape. The URL parser preserves that invalid encoding,
  // but react-router throws when it decodes the pathname during render. Keep
  // malformed pathnames away from react-router; Link treats the absolute URL
  // as a normal anchor instead.
  try {
    decodeURI(url.pathname);
  } catch {
    return targetHref;
  }

  // Markdown contains browser URLs, whereas the router expects app paths.
  // The router's root href includes its mount prefix, when there is one.
  const basePath = routerRoot.replace(/\/$/, '');
  if (url.pathname !== basePath && !url.pathname.startsWith(`${basePath}/`)) {
    return targetHref;
  }
  const pathname = url.pathname.slice(basePath.length) || '/';
  return pathname + url.search + url.hash;
}
